import React from 'react';
import DatePicker from 'react-datepicker';
import { FiEdit3, FiPaperclip, FiX } from 'react-icons/fi';
import { components, OptionProps, StylesConfig } from 'react-select';

import {
    Button,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Select,
    Spinner,
    TextArea
} from 'common/materials';
import { SelectOption } from 'data/ticketCreation/schemas';

import useCreateTicketViewModel from './CreateTicket.viewmodel';
import TicketDraftSelector from './TicketDraftSelector';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onCreated?: () => void | Promise<void>;
};

function RequiredLabel({ children }: { children: React.ReactNode }) {
    return (
        <Label className="mb-1 font-medium">
            {children} <span className="text-red-500">*</span>
        </Label>
    );
}

function ParticipantOption(props: OptionProps<SelectOption, true>) {
    return (
        <components.Option {...props}>
            <div className="flex items-center gap-2">
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    readOnly
                    tabIndex={-1}
                    className="h-4 w-4 accent-orange-600"
                />
                <span>{props.label}</span>
            </div>
        </components.Option>
    );
}

const selectedPersonColor = '#D1EAF5';

const assigneeSelectStyles: StylesConfig<SelectOption, false> = {
    option: (baseStyles, state) => ({
        ...baseStyles,
        ...(state.isSelected && {
            backgroundColor: selectedPersonColor,
            color: '#1f2937'
        })
    })
};

const participantSelectStyles: StylesConfig<SelectOption, true> = {
    option: (baseStyles, state) => ({
        ...baseStyles,
        ...(state.isSelected && {
            backgroundColor: selectedPersonColor,
            color: '#1f2937'
        })
    })
};

function rankParticipantOptions(options: SelectOption[], search: string) {
    const normalizedSearch = search.trim().toLocaleLowerCase();
    if (!normalizedSearch) return options;

    return options
        .map(option => {
            const normalizedLabel = option.label.trim().toLocaleLowerCase();
            let rank = Number.POSITIVE_INFINITY;

            if (normalizedLabel === normalizedSearch) {
                rank = 0;
            } else if (normalizedLabel.startsWith(normalizedSearch)) {
                rank = 1;
            } else if (
                normalizedLabel
                    .split(/\s+/)
                    .some(namePart => namePart.startsWith(normalizedSearch))
            ) {
                rank = 2;
            } else if (normalizedLabel.includes(normalizedSearch)) {
                rank = 3;
            }

            return { option, rank };
        })
        .filter(result => Number.isFinite(result.rank))
        .sort(
            (left, right) =>
                left.rank - right.rank ||
                left.option.label.localeCompare(right.option.label, ['th', 'en'], {
                    sensitivity: 'base',
                    numeric: true
                })
        )
        .map(result => result.option);
}

function formatFileSize(size: number) {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function CreateTicketModal(props: Props) {
    const viewModel = useCreateTicketViewModel(props);
    const [participantSearch, setParticipantSearch] = React.useState('');
    const {
        form,
        attachments,
        attachmentWarning,
        drafts,
        selectedDraft,
        ownerName,
        workGroupOptions,
        subWorkGroupOptions,
        hasteOptions,
        assigneeOptions,
        optionsError,
        workGroupUsersError,
        assignmentPreferenceWarning,
        draftsError,
        draftWarning,
        dateError,
        isDirty,
        isLoadingOptions,
        isLoadingWorkGroupUsers,
        isLoadingDrafts,
        isLoadingDraft,
        isSaving,
        isDeleting,
        isSubmitting,
        canSubmit,
        updateField,
        handleWorkGroupChange,
        handleAddAttachments,
        handleRemoveAttachment,
        handleSelectDraft,
        handleNewDraft,
        handleSaveDraft,
        handleDeleteDraft,
        handleSubmit,
        handleRequestClose,
        retryOptions,
        retryWorkGroupUsers,
        retryDrafts
    } = viewModel;

    const selectedWorkGroup =
        workGroupOptions.find(option => option.value === form.workGroupCode) ?? null;
    const selectedSubWorkGroup =
        subWorkGroupOptions.find(option => option.value === form.subWorkGroupCode) ?? null;
    const selectedAssignee =
        assigneeOptions.find(option => option.value === form.firstAssignId) ?? null;
    const selectedParticipants = assigneeOptions.filter(option =>
        form.participantIds.includes(option.value)
    );
    const participantOptions = React.useMemo(
        () => rankParticipantOptions(assigneeOptions, participantSearch),
        [assigneeOptions, participantSearch]
    );

    React.useEffect(() => {
        setParticipantSearch('');
    }, [form.workGroupCode, props.isOpen]);

    return (
        <Modal
            isOpen={props.isOpen}
            showCloseButton
            onClose={() => void handleRequestClose()}
            className="max-h-[calc(100vh-80px)] w-[calc(100vw-48px)] max-w-[1280px] overflow-hidden p-0"
        >
            <ModalHeader className="flex-wrap items-center gap-4 border-b px-6 py-4 pr-12">
                <div className="flex items-center gap-2">
                    <FiEdit3 className="text-primary-900" />
                    <div>
                        <p className="text-xl font-semibold">
                            Internal Work Assignment
                        </p>
                        <p className="text-xs font-normal text-neutral-500">
                            สร้าง Ticket เข้าระบบโดยตรง
                        </p>
                    </div>
                </div>

                <TicketDraftSelector
                    drafts={drafts}
                    selectedDraftId={selectedDraft?.draft_id}
                    isLoading={isLoadingDrafts || isLoadingDraft || isSubmitting}
                    isDeleting={isDeleting}
                    onSelect={draftId => void handleSelectDraft(draftId)}
                    onNew={() => void handleNewDraft()}
                    onDelete={() => void handleDeleteDraft()}
                />
            </ModalHeader>

            <ModalBody className="m-0 overflow-y-auto rounded-none px-6 py-5">
                {optionsError && (
                    <div className="mb-4 flex items-center justify-between gap-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        <span>{optionsError}</span>
                        <Button
                            outline
                            className="shrink-0 px-3"
                            onClick={() => void retryOptions()}
                        >
                            ลองใหม่
                        </Button>
                    </div>
                )}

                {draftsError && (
                    <div className="mb-4 flex items-center justify-between gap-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        <span>โหลดรายการงานร่างไม่สำเร็จ: {draftsError}</span>
                        <Button
                            outline
                            className="shrink-0 px-3"
                            onClick={() => void retryDrafts()}
                        >
                            ลองใหม่
                        </Button>
                    </div>
                )}

                {draftWarning && (
                    <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        ข้อมูลบางส่วนในงานร่างถูกล้าง เนื่องจากใช้ไม่ได้กับข้อมูลล่าสุด:{' '}
                        {draftWarning}
                    </div>
                )}

                {isLoadingOptions && !workGroupOptions.length ? (
                    <div className="flex min-h-[360px] items-center justify-center gap-3 text-neutral-500">
                        <Spinner /> กำลังโหลดข้อมูลสำหรับเปิดงาน...
                    </div>
                ) : (
                    <div className="space-y-5">
                        <section>
                            <div className="mb-3 flex items-center gap-5">
                                <p className="text-base font-semibold text-neutral-800">
                                    ข้อมูลงาน
                                </p>
                                <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-neutral-700">
                                    <input
                                        type="checkbox"
                                        checked={form.sendEmail}
                                        disabled={isSubmitting}
                                        onChange={event =>
                                            updateField('sendEmail', event.target.checked)
                                        }
                                        className="h-4 w-4 accent-orange-600"
                                    />
                                    <span>ส่งอีเมลแจ้งเตือน</span>
                                </label>
                            </div>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <RequiredLabel>Work Group</RequiredLabel>
                                    <Select<SelectOption>
                                        value={selectedWorkGroup}
                                        options={workGroupOptions}
                                        placeholder="เลือก Work Group"
                                        isDisabled={isSubmitting}
                                        onChange={option =>
                                            handleWorkGroupChange(option?.value ?? '')
                                        }
                                        isClearable
                                    />
                                </div>
                                <div>
                                    <Label className="mb-1 font-medium">Sub Work Group</Label>
                                    <Select<SelectOption>
                                        value={selectedSubWorkGroup}
                                        options={subWorkGroupOptions}
                                        placeholder={
                                            form.workGroupCode
                                                ? 'เลือก Sub Work Group'
                                                : 'กรุณาเลือก Work Group ก่อน'
                                        }
                                        isDisabled={!form.workGroupCode || isSubmitting}
                                        onChange={option =>
                                            updateField('subWorkGroupCode', option?.value ?? '')
                                        }
                                        isClearable
                                    />
                                </div>
                            </div>

                            <div className="mt-4">
                                <RequiredLabel>หัวข้องานที่จะเปิด</RequiredLabel>
                                <Input
                                    maxLength={1000}
                                    value={form.subject}
                                    disabled={isSubmitting}
                                    placeholder="ระบุหัวข้องานที่จะเปิด"
                                    onChange={event => updateField('subject', event.target.value)}
                                />
                                <p className="mt-1 text-right text-xs text-neutral-400">
                                    {form.subject.length}/1000
                                </p>
                            </div>

                            <div className="mt-4">
                                <Label className="mb-1 font-medium">รายละเอียดงาน</Label>
                                <TextArea
                                    rows={3}
                                    maxLength={3000}
                                    value={form.detail}
                                    disabled={isSubmitting}
                                    placeholder="อธิบายงานที่ต้องดำเนินการ"
                                    onChange={event => updateField('detail', event.target.value)}
                                />
                                <p className="mt-1 text-right text-xs text-neutral-400">
                                    {form.detail.length}/3000
                                </p>
                            </div>

                            <div className="mt-4">
                                <Label className="font-medium">ไฟล์แนบ</Label>
                                <div className="mt-3">
                                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm text-neutral-700 transition hover:border-primary-500 disabled:cursor-not-allowed">
                                        <FiPaperclip />
                                        เลือกไฟล์
                                        <input
                                            type="file"
                                            multiple
                                            disabled={isSubmitting}
                                            className="hidden"
                                            onChange={event => {
                                                handleAddAttachments(event.currentTarget.files);
                                                event.currentTarget.value = '';
                                            }}
                                        />
                                    </label>
                                </div>
                                <p className="mt-1 text-xs text-neutral-400">
                                    เลือกได้หลายไฟล์ • ไฟล์แนบจะไม่ถูกบันทึกพร้อมงานร่าง
                                </p>
                                {attachmentWarning && (
                                    <p className="mt-1 text-xs text-amber-700">
                                        {attachmentWarning}
                                    </p>
                                )}

                                {attachments.length > 0 && (
                                    <div className="mt-3 space-y-2">
                                        {attachments.map((file, index) => (
                                            <div
                                                key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                                                className="flex items-center gap-3 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm"
                                            >
                                                <FiPaperclip className="shrink-0 text-neutral-500" />
                                                <span
                                                    title={file.name}
                                                    className="min-w-0 flex-1 truncate text-neutral-700"
                                                >
                                                    {file.name}
                                                </span>
                                                <span className="shrink-0 text-xs text-neutral-400">
                                                    {formatFileSize(file.size)}
                                                </span>
                                                <button
                                                    type="button"
                                                    disabled={isSubmitting}
                                                    aria-label={`นำไฟล์ ${file.name} ออก`}
                                                    onClick={() => handleRemoveAttachment(index)}
                                                    className="shrink-0 rounded p-1 text-neutral-500 hover:bg-neutral-200 hover:text-red-600 disabled:opacity-50"
                                                >
                                                    <FiX />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </section>

                        <section className="border-t pt-5">
                            <p className="mb-3 text-base font-semibold text-neutral-800">
                                มอบหมายผู้รับผิดชอบ
                            </p>
                            {workGroupUsersError && (
                                <div className="mb-4 flex items-center justify-between gap-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                    <span>{workGroupUsersError}</span>
                                    <Button
                                        outline
                                        className="shrink-0 px-3"
                                        onClick={retryWorkGroupUsers}
                                    >
                                        ลองใหม่
                                    </Button>
                                </div>
                            )}
                            {assignmentPreferenceWarning && !workGroupUsersError && (
                                <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                                    โหลดค่าผู้รับมอบหมายล่าสุดไม่สำเร็จ สามารถเลือกใหม่ได้ตามปกติ:{' '}
                                    {assignmentPreferenceWarning}
                                </div>
                            )}
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <RequiredLabel>Owner</RequiredLabel>
                                    <Input
                                        value={ownerName}
                                        disabled
                                        className="bg-neutral-50"
                                    />
                                </div>
                                <div>
                                    <RequiredLabel>ผู้รับมอบหมาย</RequiredLabel>
                                    <Select<SelectOption>
                                        value={selectedAssignee}
                                        options={assigneeOptions}
                                        styles={assigneeSelectStyles}
                                        placeholder={
                                            !form.workGroupCode
                                                ? 'กรุณาเลือก Work Group ก่อน'
                                                : isLoadingWorkGroupUsers
                                                ? 'กำลังโหลดรายชื่อ...'
                                                : 'ค้นหาผู้รับมอบหมาย'
                                        }
                                        isLoading={isLoadingWorkGroupUsers}
                                        isDisabled={
                                            !form.workGroupCode ||
                                            isLoadingWorkGroupUsers ||
                                            !!workGroupUsersError ||
                                            isSubmitting
                                        }
                                        onChange={option =>
                                            updateField('firstAssignId', option?.value ?? '')
                                        }
                                        isClearable
                                    />
                                </div>
                            </div>

                            <div className="mt-4">
                                <Label className="mb-1 font-medium">ผู้เกี่ยวข้อง</Label>
                                <Select<SelectOption, true>
                                    isMulti
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    components={{ Option: ParticipantOption }}
                                    value={selectedParticipants}
                                    options={participantOptions}
                                    styles={participantSelectStyles}
                                    inputValue={participantSearch}
                                    onInputChange={(value, actionMeta) => {
                                        if (actionMeta.action === 'input-change') {
                                            setParticipantSearch(value);
                                        } else if (actionMeta.action === 'menu-close') {
                                            setParticipantSearch('');
                                        }
                                    }}
                                    placeholder={
                                        !form.workGroupCode
                                            ? 'กรุณาเลือก Work Group ก่อน'
                                            : isLoadingWorkGroupUsers
                                            ? 'กำลังโหลดรายชื่อ...'
                                            : 'ค้นหาและเลือกผู้เกี่ยวข้อง'
                                    }
                                    isLoading={isLoadingWorkGroupUsers}
                                    isDisabled={
                                        !form.workGroupCode ||
                                        isLoadingWorkGroupUsers ||
                                        !!workGroupUsersError ||
                                        isSubmitting
                                    }
                                    onChange={options =>
                                        updateField(
                                            'participantIds',
                                            options.map(option => option.value)
                                        )
                                    }
                                    isClearable
                                />
                                <p className="mt-1 text-xs text-neutral-400">
                                    เลือกได้หลายคนจากรายชื่อใน Work Group
                                </p>
                            </div>
                        </section>

                        <section className="border-t pt-5">
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <RequiredLabel>วันและเวลาเริ่ม</RequiredLabel>
                                    <DatePicker
                                        selected={form.startAt}
                                        disabled={isSubmitting}
                                        onChange={date => date && updateField('startAt', date)}
                                        showTimeSelect
                                        timeFormat="HH:mm"
                                        timeIntervals={15}
                                        timeCaption="เวลา"
                                        dateFormat="dd/MM/yyyy HH:mm"
                                        customInput={<Input />}
                                    />
                                </div>
                                <div>
                                    <RequiredLabel>วันและเวลาสิ้นสุด</RequiredLabel>
                                    <DatePicker
                                        selected={form.endAt}
                                        disabled={isSubmitting}
                                        onChange={date => date && updateField('endAt', date)}
                                        showTimeSelect
                                        timeFormat="HH:mm"
                                        timeIntervals={15}
                                        timeCaption="เวลา"
                                        dateFormat="dd/MM/yyyy HH:mm"
                                        customInput={<Input />}
                                    />
                                    {dateError && (
                                        <p className="mt-1 text-xs text-red-600">{dateError}</p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-4">
                                <Label className="mb-2 font-medium">ความเร่งด่วน</Label>
                                {hasteOptions.length ? (
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                        {hasteOptions.map(haste => (
                                            <button
                                                key={haste.haste_code}
                                                type="button"
                                                disabled={isSubmitting}
                                                className={`rounded-md border px-4 py-2 text-sm transition-colors ${
                                                    form.hasteCode === haste.haste_code
                                                        ? 'border-primary-900 bg-primary-50 font-medium text-primary-900'
                                                        : 'border-neutral-300 bg-white text-neutral-700 hover:border-primary-500'
                                                }`}
                                                onClick={() =>
                                                    updateField('hasteCode', haste.haste_code)
                                                }
                                            >
                                                {haste.description}
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="rounded-md border border-dashed px-4 py-3 text-sm text-neutral-500">
                                        ไม่พบข้อมูลความเร่งด่วน
                                    </p>
                                )}
                            </div>

                        </section>
                    </div>
                )}
            </ModalBody>

            <ModalFooter className="m-0 border-t px-6 py-4">
                <span className="mr-auto self-center text-xs text-neutral-500">
                    {selectedDraft
                        ? `กำลังแก้ไขงานร่าง${isDirty ? ' • มีข้อมูลที่ยังไม่บันทึก' : ''}`
                        : isDirty
                        ? 'มีข้อมูลที่ยังไม่บันทึก'
                        : ''}
                </span>
                <Button
                    outline
                    disabled={isSubmitting}
                    onClick={() => void handleRequestClose()}
                >
                    ยกเลิก
                </Button>
                <Button
                    outline
                    disabled={isSaving || isSubmitting || !form.taskOwnerId || !!dateError}
                    className="gap-2 border-primary-900 text-primary-900"
                    onClick={() => void handleSaveDraft()}
                >
                    {isSaving && <Spinner />}
                    บันทึกร่าง
                </Button>
                <Button
                    disabled={!canSubmit}
                    className="gap-2 bg-primary-900 text-white"
                    onClick={() => void handleSubmit()}
                >
                    {isSubmitting && <Spinner />}
                    มอบหมายงาน
                </Button>
            </ModalFooter>
        </Modal>
    );
}

export default CreateTicketModal;
